import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, onSessionRejected, onSessionRenewed, setSession } from './api';
import type { LoginResponse } from './types';

/**
 * Console session state.
 *
 * The token lives in `sessionStorage`, not `localStorage`: an operator console that hands
 * out credentials should not leave a valid session behind in a closed tab, and a token
 * that survives a browser restart is a token nobody remembers they still have.
 */

const STORAGE_KEY = 'rabit.console.session';

export interface ConsoleSession {
  token: string;
  workspaceId: string;
  workspaces: LoginResponse['workspaces'];
  user: LoginResponse['user'];
}

interface AuthValue {
  session: ConsoleSession | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  switchWorkspace: (workspaceId: string) => void;
}

const AuthContext = createContext<AuthValue>({
  session: null,
  login: async () => undefined,
  logout: () => undefined,
  switchWorkspace: () => undefined,
});

function restore(): ConsoleSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ConsoleSession) : null;
  } catch {
    return null;
  }
}

/**
 * Registers the session with the api module *synchronously*.
 *
 * This cannot be done in an effect. React runs child effects before parent effects, so a
 * page's first fetch would go out before AuthProvider's effect had set the token — which
 * is exactly what happened: every screen 401'd on first paint and only recovered on its
 * next poll. Applying it as state changes keeps the token ahead of every request.
 */
function applySession(session: ConsoleSession | null): ConsoleSession | null {
  setSession(session ? { token: session.token, workspaceId: session.workspaceId } : null);
  return session;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setLocalSession] = useState<ConsoleSession | null>(() => applySession(restore()));

  // Sunucu jetonu reddettiğinde oturumu düşür. Yönlendirmeye gerek yok: `gate()` zaten
  // oturumsuz her yolu girişe çeviriyor, yani tek doğru kaynak burası kalıyor.
  useEffect(() => {
    onSessionRejected(() => setLocalSession(applySession(null)));
    // Tazelenen jetonu sakla; `applySession` çağrılmıyor çünkü `api` kendi kopyasını
    // zaten güncelledi ve tekrar yazmak aynı değeri iki kez kurmak olurdu.
    onSessionRenewed((token) =>
      setLocalSession((current) => (current ? { ...current, token } : current)),
    );
    return () => {
      onSessionRejected(null);
      onSessionRenewed(null);
    };
  }, []);

  // Persistence only — the token itself is registered synchronously by `applySession`.
  useEffect(() => {
    if (session) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else sessionStorage.removeItem(STORAGE_KEY);
  }, [session]);

  const login = useCallback(async (email: string, password: string) => {
    const response = await api.post<LoginResponse>('/v1/auth/login', { email, password });
    const workspace = response.workspaces[0];
    if (!workspace) throw new Error('This account belongs to no workspace.');
    setLocalSession(
      applySession({
        token: response.accessToken,
        workspaceId: workspace.id,
        workspaces: response.workspaces,
        user: response.user,
      }),
    );
  }, []);

  const logout = useCallback(() => setLocalSession(applySession(null)), []);

  const switchWorkspace = useCallback((workspaceId: string) => {
    setLocalSession((current) => (current ? applySession({ ...current, workspaceId }) : current));
  }, []);

  const value = useMemo(
    () => ({ session, login, logout, switchWorkspace }),
    [session, login, logout, switchWorkspace],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  return useContext(AuthContext);
}
